import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  HistoricalKpiResponse,
  Kpi,
  KpiChartDataResponse,
  KpiConfig,
  KpiKeys,
  KpisByKey
} from '@models';
import { UserDataService } from '@services';
import { environment, featureFlags } from 'environments/environment';
import { Observable, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KpiService {
  private http: HttpClient = inject(HttpClient);
  private userDataService: UserDataService = inject(UserDataService);
  private BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;

  getKpi(studySk: string, kpiKey: KpiKeys, browserTimezone: string): Observable<Kpi> {
    const GET_KPI_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/kpi/`;
    const commonParams = this.prepareCommonParams();
    const params = commonParams
      .set('study_sk', studySk)
      .set('kpi', kpiKey)
      .set('browser_timezone', browserTimezone);

    return this.http.get<Kpi>(GET_KPI_URL, {
      withCredentials: true,
      params
    });
  }

  getKpiAll(studySk: string, kpiKey: KpiKeys[], browserTimezone: string): Observable<KpisByKey> {
    const GET_KPI_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/kpi/all`;
    const commonParams = this.prepareCommonParams();
    let params = commonParams.set('study_sk', studySk);

    // Filter out hidden KPIs based on environment configuration
    const filteredKpiKeys = kpiKey.filter((key) => !featureFlags.hiddenKPIs.includes(key));

    filteredKpiKeys.forEach((key) => {
      params = params.append('kpis', key);
    });
    params = params.append('browser_timezone', browserTimezone);
    return this.http.get<KpisByKey>(GET_KPI_URL, {
      withCredentials: true,
      params
    });
  }

  getHistoricalKpi(
    studySk: string,
    kpiKey: KpiKeys,
    browserTimezone: string
  ): Observable<HistoricalKpiResponse> {
    const TIMEOUT = environment.KPI_CHART_DATA_TIMEOUT;
    const GET_HISTORICAL_KPI_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/kpi/historical`;
    const commonParams = this.prepareCommonParams();
    const params = commonParams
      .set('study_sk', studySk)
      .set('kpi', kpiKey)
      .set('browser_timezone', browserTimezone);

    return this.http
      .get<HistoricalKpiResponse>(GET_HISTORICAL_KPI_URL, {
        withCredentials: true,
        params
      })
      .pipe(timeout(TIMEOUT)) as Observable<HistoricalKpiResponse>;
  }

  getKpiConfig(): Observable<KpiConfig> {
    const GET_KPI_CONFIG_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/kpi/config`;

    return this.http.get<KpiConfig>(GET_KPI_CONFIG_URL, {
      withCredentials: true
    });
  }

  getKpiChartData(studySk: string, kpiKey: KpiKeys): Observable<KpiChartDataResponse> {
    const TIMEOUT = environment.KPI_CHART_DATA_TIMEOUT;
    const GET_KPI_CHART_DATA_URL = `${this.BASE_URL}/crg-sponsor-portal-service/api/v1/kpi/chart-data`;
    const commonParams = this.prepareCommonParams();
    const params = commonParams.set('study_sk', studySk).set('kpi', kpiKey);

    return this.http
      .get<KpiChartDataResponse>(GET_KPI_CHART_DATA_URL, {
        withCredentials: true,
        params
      })
      .pipe(timeout(TIMEOUT)) as Observable<KpiChartDataResponse>;
  }

  private prepareCommonParams(): HttpParams {
    const clientNames = this.userDataService.getStandardClientNames();
    const orgIds = this.userDataService.getUserOrgIds();
    const username = this.userDataService.getUsername();
    const standardClientNamesJson = encodeURIComponent(JSON.stringify(clientNames ?? []));
    let params: HttpParams = new HttpParams();

    params = params.set('org_ids', orgIds?.join(',') ?? '');
    params = params.set('user_id', username ?? '');
    params = params.set('standard_client_names', standardClientNamesJson);

    return params;
  }
}
